import { defaultContractBody } from '@/utils/contract';

export interface ContentTypeOption {
  id: string;
  label: string;
  emoji: string;
  platform: string | null;
  keywords: string[];
}

export const contentTypeOptions: ContentTypeOption[] = [
  {
    id: 'instagram-post',
    label: 'Instagram Post',
    emoji: '🎥',
    platform: 'Instagram',
    keywords: ['instagram', 'post', 'photo', 'photos', 'photography', 'static post', 'carousel', 'image', 'feed'],
  },
  {
    id: 'instagram-reel',
    label: 'Instagram Reel',
    emoji: '🎥',
    platform: 'Instagram',
    keywords: ['reel', 'reels', 'instagram', 'short video', 'vertical video', 'video', 'shortform', 'short-form', 'dance'],
  },
  {
    id: 'instagram-story',
    label: 'Instagram Story',
    emoji: '🎥',
    platform: 'Instagram',
    keywords: ['story', 'stories', 'instagram', 'ephemeral', 'swipe up', '24 hour', 'takeover'],
  },
  {
    id: 'tiktok-video',
    label: 'TikTok Video',
    emoji: '🎵',
    platform: 'TikTok',
    keywords: ['tiktok', 'short video', 'vertical video', 'video', 'shortform', 'short-form', 'trend', 'sound', 'dance', 'live'],
  },
  {
    id: 'youtube-video',
    label: 'YouTube Video',
    emoji: '▶️',
    platform: 'YouTube',
    keywords: ['youtube', 'video', 'long form', 'long-form', 'review', 'vlog', 'tutorial', 'unboxing', 'explainer', 'live', 'live stream'],
  },
  {
    id: 'youtube-shorts',
    label: 'YouTube Shorts',
    emoji: '▶️',
    platform: 'YouTube',
    keywords: ['shorts', 'youtube', 'short video', 'vertical video', 'video', 'shortform', 'short-form'],
  },
  {
    id: 'x-post',
    label: 'X Post',
    emoji: '𝕏',
    platform: 'X',
    keywords: ['x', 'twitter', 'thread', 'tweet', 'post', 'text'],
  },
  {
    id: 'facebook-post',
    label: 'Facebook Post',
    emoji: '👍',
    platform: 'Facebook',
    keywords: ['facebook', 'post', 'community', 'group'],
  },
  {
    id: 'linkedin-post',
    label: 'LinkedIn Post',
    emoji: '💼',
    platform: null,
    keywords: ['linkedin', 'professional', 'b2b', 'thought leadership', 'article'],
  },
  {
    id: 'blog-post',
    label: 'Blog Post',
    emoji: '🖌️',
    platform: null,
    keywords: ['blog', 'article', 'written', 'seo', 'long form', 'long-form', 'newsletter', 'guide', 'review', 'recipe'],
  },
  {
    id: 'podcast-mention',
    label: 'Podcast Mention',
    emoji: '🎙️',
    platform: null,
    keywords: ['podcast', 'audio', 'interview', 'mention', 'host read', 'radio'],
  },
  {
    id: 'event-appearance',
    label: 'Event Appearance',
    emoji: '📍',
    platform: null,
    keywords: ['event', 'appearance', 'in person', 'in-person', 'live event', 'activation', 'roadshow', 'pop-up', 'popup', 'meet and greet', 'meetup', 'host', 'mc', 'attendance', 'launch party'],
  },
  { id: 'other', label: 'Other', emoji: '📌', platform: null, keywords: [] },
];

export interface CampaignBrief {
  name: string;
  brandClient: string;
  objective: string;
  description: string;
  deliverables: string;
  markets: string[];
  contentTypes: string[];
  customContentType: string;
  budget: number;
  currency: string;
  startDate: string;
  endDate: string;
  linkExpiresOn: string;
  inviteOnly: boolean;
}

export const emptyBrief: CampaignBrief = {
  name: '',
  brandClient: '',
  objective: '',
  description: '',
  deliverables: '',
  markets: [],
  contentTypes: [],
  customContentType: '',
  budget: 0,
  currency: 'KES',
  startDate: '',
  endDate: '',
  linkExpiresOn: '',
  // Off by default, as the live portal's form and the column: invite-only campaigns
  // refuse the onboarding link.
  inviteOnly: false,
};

export const briefExample: CampaignBrief = {
  name: 'Ubuntu Naturals SPF Launch',
  brandClient: 'Ubuntu Naturals',
  objective:
    'Launch a mineral SPF range to urban women aged 20-34 across Kenya and Uganda with tutorial-led creator content that drives first purchase.',
  description:
    'Ubuntu Naturals is launching its first mineral SPF range and wants authentic, tutorial-led content that shows the product in an everyday urban routine. Highlight the lightweight, no-white-cast finish and drive first-time purchases through your personal link.',
  deliverables:
    '1× Instagram Reel (60s min)\n3× Stories with swipe-up link\n1× Feed post tagging @ubuntunaturals',
  markets: ['Kenya'],
  contentTypes: ['instagram-reel', 'tiktok-video'],
  customContentType: '',
  budget: 1500000,
  currency: 'KES',
  startDate: '2026-10-01',
  endDate: '2026-11-30',
  linkExpiresOn: '',
  inviteOnly: true,
};

export type AttachmentKind = 'document' | 'image' | 'video' | 'audio';

/** A file the brand attaches to the brief (brief doc, mood board, reference, etc.). */
export interface CampaignAttachment {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  kind: AttachmentKind;
  file: File;
  previewUrl: string | null;
}

/** The contract a brand uploads and shares with hired creators. */
export interface ContractDraft {
  fileName: string;
  fileType: string;
  fileSize: string;
  body: string;
}

export const emptyContractDraft: ContractDraft = {
  fileName: '',
  fileType: '',
  fileSize: '',
  body: defaultContractBody,
};